// SKIM History Page - Integrated into CI Frontend App
// Converted from TypeScript to JavaScript with @awsui components

import React, { useState, useEffect } from 'react';
import {
  Container,
  Header,
  Table,
  Button,
  Box,
  SpaceBetween,
  StatusIndicator,
  Badge,
  Link,
  Popover,
  Alert,
  Pagination,
  Toggle,
} from '@awsui/components-react';
import { skimApi } from './skimApi';
import SkimVerificationPanel from './SkimVerificationPanel';
import { config } from '../utils/config';

const VERIFY_ENABLED = config.skimVerifyEnabled ? config.skimVerifyEnabled() : false;

const SkimHistoryPage = ({ onNavigate }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nextToken, setNextToken] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  // Deleted rows are soft-deleted (out of search, original retained for audit
  // and download) — hidden by default, shown via the toggle. This also makes
  // delete consistent: before, a deleted row vanished from the current view
  // then reappeared on the next load (decision 2026-09-21, option b).
  const [showDeleted, setShowDeleted] = useState(false);
  // Item 16: batch delete — multi-select rows, one delete action.
  const [selectedRows, setSelectedRows] = useState([]);
  const PAGE_SIZE = 10;

  const [selectedJob, setSelectedJob] = useState(null);
  const [jobDetail, setJobDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const fetchJobs = async (token) => {
    try {
      setLoading(true);
      setError(null);

      const params = { limit: 20 };
      if (token) {
        params.next_token = token;
      }

      const response = await skimApi.getHistory(params);

      if (token) {
        // Append to existing jobs (pagination)
        setJobs((prev) => [...prev, ...response.jobs]);
      } else {
        // Replace jobs (new filter)
        setJobs(response.jobs);
      }

      setNextToken(response.next_token || null);
      setHasMore(!!response.next_token);
    } catch (err) {
      console.error('Error fetching history:', err);
      setError(err.message || 'Could not load your uploads. Your session may have expired — please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  const handleLoadMore = () => {
    if (nextToken) {
      fetchJobs(nextToken);
    }
  };

  const handleDownload = async (jobId, filename) => {
    try {
      const response = await skimApi.getDownloadUrl(jobId);
      window.open(response.download_url, '_blank');
    } catch (err) {
      console.error('Error downloading file:', err);
      alert(`Failed to download: ${err.message || 'Unknown error'}`);
    }
  };

  const handleDelete = async (jobId) => {
    if (!confirm('Are you sure you want to delete this job? The original file will be retained for audit.')) {
      return;
    }

    try {
      await skimApi.deleteJob(jobId);
      // Mark DELETED in local state (review finding, CR-306672780): removing the
      // row entirely recreated the exact vanish-then-reappear this CR fixes —
      // hidden by the default Show-deleted=off, visible when toggled, and
      // consistent with what the next fetch returns. Same as the batch path.
      setJobs((prev) => prev.map((job) => job.job_id === jobId ? { ...job, status: 'DELETED' } : job));
      // Review finding (rev 3): selectedRows still held the OLD object (status
      // PUBLISHED), so the batch control kept counting a just-deleted row and
      // batch delete would re-issue deleteJob on it. Drop it from the selection.
      setSelectedRows((prev) => prev.filter((row) => row.job_id !== jobId));
    } catch (err) {
      console.error('Error deleting job:', err);
      alert(`Failed to delete: ${err.message || 'Unknown error'}`);
    }
  };

  const handleConvert = async (jobId) => {
    if (!confirm(
      'Convert this Point-in-time document to Maintained Content? ' +
      'It will be fact-checked now and re-verified on a schedule. This cannot be undone.'
    )) {
      return;
    }
    try {
      const result = await skimApi.convertJob(jobId);
      // Server's outcome sentence verbatim (convert.py).
      alert(result.message || 'Document converted to Maintained.');
      fetchJobs();
    } catch (err) {
      console.error('Error converting job:', err);
      alert(`Failed to convert: ${err.message || 'Unknown error'}`);
    }
  };

  const handleRestore = async (jobId) => {
    try {
      const result = await skimApi.restoreJob(jobId);
      // Server's outcome sentence verbatim (restore.py): attested -> active;
      // checked -> re-verification starts.
      alert(result.message || 'Document restored.');
      fetchJobs();
    } catch (err) {
      console.error('Error restoring job:', err);
      alert(`Failed to restore: ${err.message || 'Unknown error'}`);
    }
  };

  const handleExtend = async (jobId) => {
    if (!confirm('Extend this document 30 days from today? Its archive date will be replaced.')) {
      return;
    }
    try {
      const result = await skimApi.extendTtl(jobId, 30);
      alert(`New archive date: ${(result.new_expiry || '').slice(0, 10)}`);
      fetchJobs();
    } catch (err) {
      console.error('Error extending TTL:', err);
      alert(`Failed to extend: ${err.message || 'Unknown error'}`);
    }
  };

  // Item 16 (batch delete) design guards from the fix-list: the confirmation
  // lists the filenames; outcomes are reported PER ROW — a PROCESSING row's
  // refusal (delete.py:56-61) must name the file, never fail the batch silently
  // (F47's lesson applied to delete). Backend needs nothing: loops the existing
  // per-job DELETE.
  const handleBatchDelete = async () => {
    const targets = selectedRows.filter((r) => r.status !== 'DELETED');
    if (targets.length === 0) return;
    const names = targets.map((r) => r.filename).join('\n  ');
    if (!confirm(
      `Delete ${targets.length} document(s)? Originals are retained for audit.\n  ${names}`
    )) {
      return;
    }
    const outcomes = [];
    for (const row of targets) {
      try {
        await skimApi.deleteJob(row.job_id);
        outcomes.push(`✓ ${row.filename} — deleted`);
      } catch (err) {
        // Server sentences surfaced verbatim, named per file (e.g. the
        // PROCESSING refusal) — never a silent partial batch.
        outcomes.push(`✗ ${row.filename} — ${err.message || 'failed'}`);
      }
    }
    alert(`Batch delete results:\n${outcomes.join('\n')}`);
    setSelectedRows([]);
    fetchJobs();
  };

  const handleViewJob = (batchId) => {
    if (onNavigate) {
      onNavigate('skim-upload', batchId);
    }
  };

  const handleOpenDetail = async (job) => {
    setSelectedJob(job);
    setLoadingDetail(true);
    try {
      const detail = await skimApi.getJobStatus(job.job_id);
      setJobDetail(detail);
    } catch (e) {
      console.error('Failed to load job detail:', e);
      setJobDetail(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  const getStatusIndicator = (status) => {
    switch (status) {
      case 'UPLOADING':
        return <StatusIndicator type="in-progress">Uploading</StatusIndicator>;
      case 'PROCESSING':
        return <StatusIndicator type="in-progress">Processing</StatusIndicator>;
      case 'PENDING_REVIEW':
        return <StatusIndicator type="pending">Pending Review</StatusIndicator>;
      case 'PUBLISHED':
        return <StatusIndicator type="success">Published</StatusIndicator>;
      case 'FAILED':
        return <StatusIndicator type="error">Failed</StatusIndicator>;
      case 'DELETED':
        return <StatusIndicator type="stopped">Deleted</StatusIndicator>;
      default:
        return <StatusIndicator>{status}</StatusIndicator>;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCompetitors = (tags) => {
    if (!tags?.competitors || tags.competitors.length === 0) {
      return <Box color="text-status-inactive">None</Box>;
    }
    
    // Show all competitors as badges
    return (
      <SpaceBetween direction="horizontal" size="xxs">
        {tags.competitors.map((comp, idx) => (
          <Badge key={idx} color="blue">
            {comp}
          </Badge>
        ))}
      </SpaceBetween>
    );
  };

  const visibleJobs = showDeleted ? jobs : jobs.filter((j) => j.status !== 'DELETED');

  const columnDefinitions = [
    {
      id: 'filename',
      header: 'Filename',
      cell: (item) => (
        <div>
          <Box fontWeight="bold">{item.filename}</Box>
          <Box variant="small" color="text-body-secondary" title={item.batch_id}>
            Batch: {item.batch_id}
          </Box>
          {/* Job ID was invisible everywhere (portal showed batch only; search returns
              none) — the Quick per-job tools need it. ADDED next to batch, never
              replacing it: batch drives the multi-file Review resume. */}
          {item.job_id && (
            <Box variant="small" color="text-body-secondary" title={item.job_id}>
              Job: {item.job_id}
            </Box>
          )}
        </div>
      ),
      sortingField: 'filename',
      width: 300,
    },
    {
      id: 'status',
      header: 'Status',
      // Archived overrides the job status: an archived doc is out of the live set
      // regardless of status — it used to sit here looking Published (item 17).
      // Active attested rows show their stored archive clock (next_action_at,
      // publish+30d, moved by extends). Both fields arrive with CR-306661354;
      // absent = renders as before.
      cell: (item) =>
        item.lifecycle_status === 'archived' ? (
          <StatusIndicator type="stopped">Archived</StatusIndicator>
        ) : (
          <div>
            {getStatusIndicator(item.status)}
            {item.route === 'attested' && item.next_action_at && (
              <Box variant="small" color="text-body-secondary">
                Archives {item.next_action_at.slice(0, 10)}
              </Box>
            )}
          </div>
        ),
      sortingField: 'status',
      width: 150,
    },
    {
      id: 'research_type',
      header: 'Research Type',
      cell: (item) => {
        // For published jobs, use approved_tags; otherwise use suggested_tags
        const researchType = item.status === 'PUBLISHED' 
          ? (item.approved_tags?.research_type || item.suggested_tags?.research_type)
          : (item.suggested_tags?.research_type || item.approved_tags?.research_type);
        return researchType ? <Box>{researchType}</Box> : <Box color="text-status-inactive">—</Box>;
      },
      width: 180,
    },
    {
      id: 'competitors',
      header: 'Competitors',
      cell: (item) => {
        // For published jobs, use approved_tags; otherwise use suggested_tags
        const tags = item.status === 'PUBLISHED'
          ? (item.approved_tags || item.suggested_tags)
          : (item.suggested_tags || item.approved_tags);
        return formatCompetitors(tags);
      },
      width: 250,
    },
    {
      id: 'depth',
      header: 'Depth',
      cell: (item) => {
        // For published jobs, use approved_tags; otherwise use suggested_tags
        const depth = item.status === 'PUBLISHED'
          ? (item.approved_tags?.research_depth || item.suggested_tags?.research_depth)
          : (item.suggested_tags?.research_depth || item.approved_tags?.research_depth);
        return depth ? <Box>{depth}</Box> : <Box color="text-status-inactive">—</Box>;
      },
      width: 100,
    },
    {
      id: 'uploaded',
      header: 'Uploaded',
      cell: (item) => <Box variant="small">{formatDate(item.created_at)}</Box>,
      sortingField: 'created_at',
      width: 180,
    },
    {
      id: 'contributors',
      header: 'Contributors',
      cell: (item) => {
        const contributors = item.additional_contributors;
        if (!contributors || contributors.length === 0) return <Box color="text-status-inactive">—</Box>;
        return <Box variant="small">{contributors.join(', ')}</Box>;
      },
      width: 150,
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: (item) => (
        <SpaceBetween direction="horizontal" size="xs">
          {/* Review & Publish Button - for PENDING_REVIEW status only */}
          {item.status === 'PENDING_REVIEW' && (
            <Button
              onClick={() => handleViewJob(item.batch_id)}
              variant="inline-link"
              fontSize="body-s"
            >
              Review
            </Button>
          )}

          {/* View Verification — for PUBLISHED maintained docs with verification flags */}
          {VERIFY_ENABLED && item.status === 'PUBLISHED' && item.route === 'checked' && (
            <Button
              onClick={() => handleOpenDetail(item)}
              variant="inline-link"
              fontSize="body-s"
            >
              Verification
            </Button>
          )}

          {/* Restore — archived docs only (the door back from automatic archival) */}
          {item.lifecycle_status === 'archived' && (
            <Button
              onClick={() => handleRestore(item.job_id)}
              variant="inline-link"
              fontSize="body-s"
            >
              Restore
            </Button>
          )}
          {/* Extend 30 days — active published Point-in-time docs (server also gates:
              owner, attested, active; ttl.py). Archived docs: restore or delete only. */}
          {item.route === 'attested' && item.status === 'PUBLISHED' && item.lifecycle_status !== 'archived' && (
            <Button
              onClick={() => handleExtend(item.job_id)}
              variant="inline-link"
              fontSize="body-s"
            >
              Extend 30 days
            </Button>
          )}
          {/* Convert to Maintained — published Point-in-time docs only (server also
              gates: owner, attested, not archived; convert.py). One-way door. */}
          {item.route === 'attested' && item.status === 'PUBLISHED' && item.lifecycle_status !== 'archived' && (
            <Button
              onClick={() => handleConvert(item.job_id)}
              variant="inline-link"
              fontSize="body-s"
            >
              Convert to Maintained
            </Button>
          )}
          {/* Download Button - always available */}
          <Button
            onClick={() => handleDownload(item.job_id, item.filename)}
            variant="icon"
            iconName="download"
            ariaLabel="Download original file"
          />

          {/* Delete Button - not for DELETED status */}
          {item.status !== 'DELETED' && (
            <>
              <Button
                onClick={() => handleDelete(item.job_id)}
                variant="icon"
                iconName="remove"
                ariaLabel="Delete job"
              />
              <Popover
                dismissButton={false}
                position="top"
                size="small"
                triggerType="custom"
                content={
                  <Box color="text-body-secondary" fontSize="body-s">
                    Deleting will remove this document from the Knowledge Base. It will no longer appear in search results.
                  </Box>
                }
              >
                <Button
                  variant="icon"
                  iconName="status-info"
                  ariaLabel="Delete info"
                />
              </Popover>
            </>
          )}
        </SpaceBetween>
      ),
      width: 250,
    },
  ];

  return (
    <Container
      header={
        <Header
          variant="h1"
          description="View and manage all your uploaded research documents"
        >
          Upload History
        </Header>
      }
    >
      <SpaceBetween direction="vertical" size="l">
        {/* Error Message */}
        {error && (
          <Alert type="error" dismissible onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Jobs Table */}
        <SpaceBetween direction="horizontal" size="l">
          <Toggle
            onChange={({ detail }) => { setShowDeleted(detail.checked); setCurrentPage(1); }}
            checked={showDeleted}
          >
            Show deleted
          </Toggle>
          <Button
            onClick={handleBatchDelete}
            disabled={selectedRows.filter((r) => r.status !== 'DELETED').length === 0}
            iconName="remove"
          >
            Delete selected ({selectedRows.filter((r) => r.status !== 'DELETED').length})
          </Button>
        </SpaceBetween>
        <Table
          selectionType="multi"
          selectedItems={selectedRows}
          onSelectionChange={({ detail }) => setSelectedRows(detail.selectedItems)}
          trackBy="job_id"
          columnDefinitions={columnDefinitions}
          items={visibleJobs.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)}
          loading={loading && jobs.length === 0}
          loadingText="Loading history..."
          empty={
            <Box textAlign="center" color="inherit">
              <Box padding={{ bottom: 's' }} variant="p" color="inherit">
                <b>No uploads yet</b>
              </Box>
              <Box variant="p" color="inherit">
                You haven't uploaded any research documents yet.
              </Box>
              <Button
                onClick={() => onNavigate && onNavigate('skim-upload')}
                variant="primary"
                iconName="upload"
              >
                Upload Your First Document
              </Button>
            </Box>
          }
          variant="container"
          pagination={
            <Pagination
              currentPageIndex={currentPage}
              pagesCount={Math.ceil(visibleJobs.length / PAGE_SIZE)}
              onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)}
            />
          }
        />

        {/* Load More from API */}
        {hasMore && (
          <Box textAlign="center">
            <Button onClick={handleLoadMore} loading={loading} disabled={loading}>
              {loading ? 'Loading...' : 'Load More'}
            </Button>
          </Box>
        )}

        {/* Job Detail Panel — verification flags for a selected PUBLISHED job */}
        {VERIFY_ENABLED && selectedJob && (
          <Container
            header={
              <Header
                variant="h2"
                actions={
                  <Button onClick={() => { setSelectedJob(null); setJobDetail(null); }}>
                    Close
                  </Button>
                }
              >
                {selectedJob.filename} — Verification
              </Header>
            }
          >
            {loadingDetail && <Box>Loading verification results...</Box>}
            {!loadingDetail && jobDetail && jobDetail.verification_results && (
              <SkimVerificationPanel
                jobId={selectedJob.job_id}
                sections={jobDetail.verification_results.sections || []}
                onSectionsChange={(updated) =>
                  setJobDetail((prev) => ({
                    ...prev,
                    verification_results: { ...prev.verification_results, sections: updated },
                  }))
                }
                onComplete={() => { setSelectedJob(null); setJobDetail(null); }}
                completeLabel="Done"
                verifyEnabled={VERIFY_ENABLED}
              />
            )}
            {!loadingDetail && jobDetail && !jobDetail.verification_results && (
              <Box color="text-status-inactive">No verification results available for this document.</Box>
            )}
            {!loadingDetail && !jobDetail && (
              <Box color="text-status-error">Failed to load job details.</Box>
            )}
          </Container>
        )}
      </SpaceBetween>
    </Container>
  );
};

export default SkimHistoryPage;
